import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { STATUS_LABEL, adminAvailabilityModifiers, availabilityClassNames } from "@/components/site/Verfuegbarkeit";
import { ArrowRightLeft, Info, Plus, RotateCcw, X } from "lucide-react";

const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const AvailabilityAdmin = () => {
    const [month, setMonth] = useState(new Date());
    const [days, setDays] = useState([]);
    const [selected, setSelected] = useState(null);
    const [capacity, setCapacity] = useState(3);
    const [blocked, setBlocked] = useState(false);
    const [note, setNote] = useState("");
    const [bookLoads, setBookLoads] = useState("1");
    const [bookName, setBookName] = useState("");
    const [bookTime, setBookTime] = useState("");
    const [moveIdx, setMoveIdx] = useState(null);
    const [moveDate, setMoveDate] = useState("");
    const [saving, setSaving] = useState(false);

    const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    const load = useCallback(() => {
        api.get(`/admin/availability?month=${monthKey}`).then((r) => setDays(r.data)).catch(() => {});
    }, [monthKey]);
    useEffect(() => {
        load();
    }, [load]);

    const dayDoc = selected ? days.find((d) => d.date === toIso(selected)) : null;

    useEffect(() => {
        setCapacity(dayDoc?.capacity ?? 3);
        setBlocked(dayDoc?.blocked ?? false);
        setNote(dayDoc?.note ?? "");
        setMoveIdx(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, days]);

    const save = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await api.put("/admin/availability", { date: toIso(selected), blocked, capacity, note: note || null });
            toast.success(`${toIso(selected)} gespeichert.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const book = async () => {
        if (!selected) return;
        try {
            const r = await api.post("/admin/availability/book", {
                date: toIso(selected),
                loads: Number(bookLoads),
                name: bookName || null,
                time: bookTime || null,
                note: note || null,
            });
            toast.success(`${fmt(toIso(selected))}: jetzt ${r.data.loads}/${r.data.capacity} Fuhren — ${STATUS_LABEL[r.data.status]}.`);
            setBookName("");
            setBookTime("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const removeTour = async (index) => {
        if (!dayDoc) return;
        try {
            await api.post("/admin/availability/tour/remove", { date: dayDoc.date, index });
            toast.success("Fuhre entfernt.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const moveTour = async (index) => {
        if (!dayDoc || !moveDate) {
            toast.error("Bitte Zieldatum wählen.");
            return;
        }
        try {
            const r = await api.post("/admin/availability/tour/move", { from_date: dayDoc.date, to_date: moveDate, index });
            toast.success(`Fuhre verschoben nach ${fmt(moveDate)} — dort jetzt ${r.loads}/${r.capacity} Fuhren.`);
            setMoveIdx(null);
            setMoveDate("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const reset = async () => {
        if (!selected) return;
        try {
            await api.delete(`/admin/availability/${toIso(selected)}`);
            toast.success("Tag zurückgesetzt — wieder frei.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-availability">
            <div className="mb-6 flex items-start gap-3 rounded border border-[#2E2517] bg-[#1B150E] p-4 text-sm text-[#9C917D]" data-testid="admin-availability-hint">
                <Info size={16} className="mt-0.5 shrink-0 text-[#C88A45]" />
                <p>
                    Am schnellsten aktuell halten Sie den Kalender über das <strong className="text-[#EDE6DA]">Postfach</strong>:
                    Anfrage annehmen, Datum und Fuhren eintragen — der Tag färbt sich automatisch.
                    Telefon-Kunden tragen Sie hier direkt ein. Jede Fuhre lässt sich per Symbol verschieben oder löschen.
                    Ein Tag ist voll, wenn die Fuhren die Kapazität erreichen (Standard: 3, bei langen Fahrtwegen auf 2 stellen).
                </p>
            </div>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                <div className="rounded border border-[#2E2517] bg-[#1B150E] p-6">
                    <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={setSelected}
                        month={month}
                        onMonthChange={setMonth}
                        modifiers={adminAvailabilityModifiers(days, month)}
                        modifiersClassNames={availabilityClassNames}
                        className="text-[#EDE6DA]"
                    />
                </div>
                <div>
                    <div className="rounded border border-[#2E2517] bg-[#1B150E] p-6">
                        <p className="mb-1 text-sm text-[#9C917D]">
                            Gewählter Tag: <span className="text-[#EDE6DA]">{selected ? selected.toLocaleDateString("de-DE") : "—"}</span>
                        </p>
                        {dayDoc && (
                            <p className="mb-4 text-sm text-[#9C917D]" data-testid="admin-day-loads">
                                Fuhren: <span className="font-semibold text-[#C88A45]">{dayDoc.loads} / {dayDoc.capacity}</span>
                                {" · "}Status: <span className="text-[#EDE6DA]">{STATUS_LABEL[dayDoc.status]}</span>
                            </p>
                        )}
                        {dayDoc && dayDoc.tours.length > 0 && (
                            <ul className="mb-4 space-y-2 rounded border border-[#2E2517] bg-[#120E09] p-3 text-xs text-[#D9D2C4]" data-testid="admin-day-tours">
                                {dayDoc.tours.map((t, i) => (
                                    <li key={i}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span>{t.time ? `${t.time} Uhr — ` : ""}{t.name}{t.place ? ` — ${t.place}` : ""}</span>
                                            <span className="flex gap-2">
                                                <button
                                                    data-testid={`tour-move-${i}`}
                                                    onClick={() => { setMoveIdx(moveIdx === i ? null : i); setMoveDate(""); }}
                                                    aria-label="Fuhre verschieben"
                                                    className="text-[#9C917D] transition-colors hover:text-[#C88A45]"
                                                >
                                                    <ArrowRightLeft size={13} />
                                                </button>
                                                <button
                                                    data-testid={`tour-remove-${i}`}
                                                    onClick={() => removeTour(i)}
                                                    aria-label="Fuhre löschen"
                                                    className="text-[#9C917D] transition-colors hover:text-red-400"
                                                >
                                                    <X size={13} />
                                                </button>
                                            </span>
                                        </div>
                                        {moveIdx === i && (
                                            <div className="mt-2 flex items-center gap-2" data-testid={`tour-move-form-${i}`}>
                                                <input
                                                    type="date"
                                                    data-testid={`tour-move-date-${i}`}
                                                    value={moveDate}
                                                    onChange={(e) => setMoveDate(e.target.value)}
                                                    className="rounded border border-[#2E2517] bg-[#1B150E] px-2 py-1.5 text-xs text-[#EDE6DA] [color-scheme:dark]"
                                                />
                                                <button
                                                    data-testid={`tour-move-save-${i}`}
                                                    onClick={() => moveTour(i)}
                                                    className="rounded-full bg-[#C88A45] px-3 py-1.5 text-xs font-bold uppercase text-[#120E09] transition-colors hover:bg-[#DBA057]"
                                                >
                                                    Verschieben
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <label className="mb-2 mt-4 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Fuhren eintragen (auch Telefon-Kunden)</label>
                        <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <Input data-testid="admin-book-name" value={bookName} onChange={(e) => setBookName(e.target.value)} placeholder="Kunde / Ort (z. B. Müller, Berlin)" className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                            <input
                                type="time"
                                data-testid="admin-book-time"
                                value={bookTime}
                                onChange={(e) => setBookTime(e.target.value)}
                                className="rounded border border-[#2E2517] bg-[#120E09] px-3 py-2 text-sm text-[#EDE6DA] [color-scheme:dark]"
                            />
                        </div>
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                            <select
                                data-testid="admin-book-loads"
                                value={bookLoads}
                                onChange={(e) => setBookLoads(e.target.value)}
                                className="rounded border border-[#2E2517] bg-[#120E09] px-3 py-2 text-sm text-[#EDE6DA]"
                            >
                                <option value="1">1 Fuhre</option>
                                <option value="2">2 Fuhren</option>
                                <option value="3">3 Fuhren</option>
                            </select>
                            <button
                                data-testid="admin-book-add"
                                onClick={book}
                                disabled={!selected}
                                className="flex items-center gap-1.5 rounded-full bg-[#7A9B5A] px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#8CAF68] disabled:opacity-40"
                            >
                                <Plus size={14} /> Eintragen
                            </button>
                        </div>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Kapazität (Fuhren pro Tag)</label>
                        <div className="mb-4 flex gap-2">
                            {[2, 3].map((c) => (
                                <button
                                    key={c}
                                    data-testid={`admin-capacity-${c}`}
                                    onClick={() => setCapacity(c)}
                                    className={`rounded-full border px-4 py-2 text-xs transition-colors duration-200 ${
                                        capacity === c ? "border-[#C88A45] bg-[#C88A45]/10 text-[#C88A45]" : "border-[#2E2517] text-[#9C917D] hover:text-[#EDE6DA]"
                                    }`}
                                >
                                    {c} Fuhren
                                </button>
                            ))}
                        </div>
                        <label className="mb-4 flex cursor-pointer items-center gap-3 text-sm text-[#EDE6DA]">
                            <input
                                type="checkbox"
                                data-testid="admin-blocked-toggle"
                                checked={blocked}
                                onChange={(e) => setBlocked(e.target.checked)}
                                className="h-4 w-4 accent-[#C88A45]"
                            />
                            Tag komplett sperren (z. B. Termine, Urlaub)
                        </label>
                        <Input data-testid="admin-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz (optional, z. B. Kundenname)" className="mb-4 border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                        <div className="flex gap-3">
                            <button
                                data-testid="admin-availability-save"
                                onClick={save}
                                disabled={!selected || saving}
                                className="flex-1 rounded-full bg-[#C88A45] py-2.5 text-sm font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057] disabled:opacity-40"
                            >
                                Speichern
                            </button>
                            {dayDoc && (
                                <button
                                    data-testid="admin-availability-reset"
                                    onClick={reset}
                                    className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-red-400/40 hover:text-red-400"
                                >
                                    <RotateCcw size={13} /> Zurücksetzen
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="mt-6 rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="admin-availability-list">
                        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Markierte Tage (dieser Monat)</p>
                        {days.length === 0 && <p className="text-sm text-[#9C917D]">Keine Einträge — alle Tage frei.</p>}
                        <div className="space-y-2">
                            {days.map((d) => (
                                <div key={d.date} className="flex items-center justify-between rounded border border-[#2E2517] px-4 py-2 text-sm" data-testid={`admin-slot-${d.date}`}>
                                    <span className="text-[#EDE6DA]">{fmt(d.date)}</span>
                                    <span className="text-xs text-[#9C917D]">
                                        {d.loads > 0 ? `${d.loads}/${d.capacity} Fuhren · ` : ""}{STATUS_LABEL[d.status]}{d.note ? ` — ${d.note}` : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
